import { DEFAULT_OPERAND, GAME_TYPES_TYPE, OPERAND_TYPE } from '../../../constants/constants';

export function performOperation({
  gameType,
  leftOperand,
  rightOperand,
}: {
  gameType: GAME_TYPES_TYPE;
  leftOperand: OPERAND_TYPE;
  rightOperand: OPERAND_TYPE;
}): OPERAND_TYPE {
  switch (gameType) {
    case GAME_TYPES_TYPE.MULTI_1_12:
    case GAME_TYPES_TYPE.MULTI_MINUS_6_6:
    case GAME_TYPES_TYPE.MULTI_1_10:
      return { a: leftOperand.a * rightOperand.a, x: 0, b: 0 };
    case GAME_TYPES_TYPE.MULTI_ALGEBRA:
      return { a: leftOperand.a * rightOperand.a, x: leftOperand.x + rightOperand.x, b: 0 };
    case GAME_TYPES_TYPE.MULTI_ALGEBRA_2_VAR:
    case GAME_TYPES_TYPE.MULTI_ALGEBRA_EXPONENTS:
      return { a: ((leftOperand.x || rightOperand.x) ? 1 : 0), x: leftOperand.x + rightOperand.x, c: ((leftOperand.y || rightOperand.y) ? 1 : 0), y: (leftOperand?.y ?? 0) + (rightOperand?.y ?? 0), b: (leftOperand.a || 1 ) * (rightOperand.a || 1) * (leftOperand.c || 1) * (rightOperand.c || 1) * (leftOperand.b || 1) * (rightOperand.b || 1) };
    case GAME_TYPES_TYPE.MULTI_FACT_GROUP_0_1_5_10:
    case GAME_TYPES_TYPE.MULTI_FACT_GROUP_1_2_4_8:
    case GAME_TYPES_TYPE.MULTI_FACT_GROUP_3_6_12:
    case GAME_TYPES_TYPE.MULTI_FACT_GROUP_7_9_11:
      return { a: 0, x: 0, c: 0, y: 0, b: leftOperand.b * rightOperand.b };
    case GAME_TYPES_TYPE.ADD_ALGEBRA:
      return { a: leftOperand.a + rightOperand.a, x: Math.max(leftOperand.x, rightOperand.x),  c: (leftOperand?.c ?? 0) + (rightOperand?.c ?? 0), y: Math.max(leftOperand?.y ?? 0, rightOperand?.y ?? 0), b: rightOperand.b + leftOperand.b };
    case GAME_TYPES_TYPE.ADD_1_12:
    case GAME_TYPES_TYPE.ADD_MINUS_6_6:
      return { a: leftOperand.a + rightOperand.a, x: 0, b: 0 };
    case GAME_TYPES_TYPE.SUBTRACT_POSITIVE_INTEGERS:
    case GAME_TYPES_TYPE.SUBTRACT_INTEGERS:
      return { a: leftOperand.a - rightOperand.a, x: 0, c: 0, y: 0, b: 0 };
    case GAME_TYPES_TYPE.DIVIDE:
      return { a: leftOperand.a / rightOperand.a, x: 0, c: 0, y: 0, b: 0 };
  }
}

// TODO: fix so that whole numbers use b instead of a.
export function getDefaultOperands(
  gameType: GAME_TYPES_TYPE
): DEFAULT_OPERAND {
  switch (gameType) {
    case GAME_TYPES_TYPE.SUBTRACT_POSITIVE_INTEGERS:
      return {
        leftOperand: { a: 13, x: 0, c: 0, y: 0, b: 0 },
        rightOperand: { a: 1, x: 0, c: 0, y: 0, b: 0 },
      }
    case GAME_TYPES_TYPE.ADD_ALGEBRA:
    case GAME_TYPES_TYPE.MULTI_FACT_GROUP_1_2_4_8:
      return {
        leftOperand: { a: 0, x: 0, c: 0, y: 0, b: 1 },
        rightOperand: { a: 0, x: 0, c: 0, y: 0, b: 1 },
      }
    case GAME_TYPES_TYPE.DIVIDE:
      return {
        leftOperand: { a: 12, x: 0, c: 0, y: 0, b: 0 },
        rightOperand: { a: 1, x: 0, c: 0, y: 0, b: 0 },
      }
    case GAME_TYPES_TYPE.MULTI_ALGEBRA_2_VAR:
      return {
        leftOperand: { a: 0, x: 0, c: 0, y: 0, b: 1 },
        rightOperand: { a: 0, x: 0, c: 0, y: 0, b: 1 },
      }  
    case GAME_TYPES_TYPE.MULTI_ALGEBRA_EXPONENTS:
      return {
        leftOperand: { a: 1, x: 1, c: 0, y: 0, b: 1 },
        rightOperand: { a: 1, x: 1, c: 0, y: 0, b: 1 },
      }
    case GAME_TYPES_TYPE.MULTI_FACT_GROUP_0_1_5_10:
      return {
        leftOperand: { a: 0, x: 0, c: 0, y: 0, b: 0 },
        rightOperand: { a: 0, x: 0, c: 0, y: 0, b: 1 },
      }
    case GAME_TYPES_TYPE.MULTI_FACT_GROUP_3_6_12:
      return {
        leftOperand: { a: 0, x: 0, c: 0, y: 0, b: 3 },
        rightOperand: { a: 0, x: 0, c: 0, y: 0, b: 1 },
      }
    case GAME_TYPES_TYPE.MULTI_FACT_GROUP_7_9_11:
      return {
        leftOperand: { a: 0, x: 0, c: 0, y: 0, b: 7 },
        rightOperand: { a: 0, x: 0, c: 0, y: 0, b: 1 },
      }
    case GAME_TYPES_TYPE.SUBTRACT_INTEGERS:
     return {
        leftOperand: { a: 1, x: 0, c: 0, y: 0, b: 0 },
        rightOperand: { a: 1, x: 0, c: 0, y: 0, b: 0 },
      }
    default:
      return {
        leftOperand: { a: 1, x: 0, b: 0 },
        rightOperand: { a: 1, x: 0, b: 0 },
      }
  }
}

const toSuperscript = (n: number): string => {
  const superscriptDigits: Record<string, string> = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻',
  };
  return n.toString().split('').map(ch => superscriptDigits[ch] || '').join('');
};

export const renderValue = (value: OPERAND_TYPE, gameType?: GAME_TYPES_TYPE): string => {
  // Special case: MULTI_ALGEBRA_2_VAR → abc𝑥𝑦 (hide x or y if exponent is 0)
  if (gameType === GAME_TYPES_TYPE.MULTI_ALGEBRA_2_VAR || gameType === GAME_TYPES_TYPE.MULTI_ALGEBRA_EXPONENTS ) {
    const rawCoeffs = [value.a, value.b, value.c];

    // Filter out 0 and undefined
    const nonZeroCoeffs = rawCoeffs.filter(n => n !== 0 && n !== undefined);

    // Multiply remaining coefficients; default to 1 if none left
    const coefficient = nonZeroCoeffs.reduce((acc, n) => acc * n, 1);

    const x = value.x ?? 0;
    const y = value.y ?? 0;

    const xTerm = x === 0 ? '' : `𝑥${x === 1 ? '' : toSuperscript(x)}`;
    const yTerm = y === 0 ? '' : `𝑦${y === 1 ? '' : toSuperscript(y)}`;

    // If both variables are missing, return coefficient
    if (!xTerm && !yTerm) {
      return `${coefficient}`;
    }

    // Omit coefficient if it's 1
    const coeffStr = coefficient === 1 ? '' : coefficient;

    return `${coeffStr}${xTerm}${yTerm}`;
  }

    // Normal rendering
  const terms: string[] = [];
  // ax^x
  if (value.a !== 0 && value.x !== 0) {
    const coeff = value.a === 1 ? '' : value.a;
    const exponent = value.x === 1 ? '' : toSuperscript(value.x);
    terms.push(`${coeff}𝑥${exponent}`);
  } else if (value.a !== 0 && value.x === 0) {
    terms.push(`${value.a}`);
  }

  // cy^y — only if c and y are defined
  if (value.c !== undefined && value.c !== null && value.c !== 0 && value.y !== undefined && value.y !== null && value.y !== 0) {
    const coeff = value.c === 1 ? '' : value.c;
    const exponent = value.y === 1 ? '' : toSuperscript(value.y);
    terms.push(`${coeff}𝑦${exponent}`);
  } else if (value.c !== undefined && value.c !== 0 && (value.y === 0 || value.y === undefined)) {
    terms.push(`${value.c}`);
  }

  // b
  if (value.b !== 0) {
    terms.push(`${value.b}`);
  }

  // If all terms are zero or missing
  if (terms.length === 0) {
    return '0';
  }
  return terms.join('+');
};
